import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { superAdminApi } from '../../services/api';

interface TenantSummary {
    id: string;
    name: string;
    slug: string;
    region?: string;
    employeeCount: number;
    maxEmployees?: number;
    subscription?: {
        status: string;
        planName?: string;
        trialEndsAt?: string;
        endsAt?: string;
    };
    createdAt: string;
}

export default function SuperAdminDashboard() {
    const [tenants, setTenants] = useState<TenantSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalTenants: 0,
        activeTenants: 0,
        trialTenants: 0,
        totalUsers: 0,
    });

    useEffect(() => {
        loadData();
    }, []);

    async function loadData() {
        try {
            const data = await superAdminApi.listTenants();
            setTenants(data.tenants || []);

            // Calculate stats
            const active = data.tenants?.filter((t: any) => t.subscription?.status === 'active').length || 0;
            const trial = data.tenants?.filter((t: any) => t.subscription?.status === 'trial').length || 0;
            const users = data.tenants?.reduce((sum: number, t: any) => sum + (t.employeeCount || 0), 0) || 0;

            setStats({
                totalTenants: data.tenants?.length || 0,
                activeTenants: active,
                trialTenants: trial,
                totalUsers: users,
            });
        } catch (error) {
            console.error('Failed to load dashboard data:', error);
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return <div className="text-center py-8">Loading dashboard...</div>;
    }

    return (
        <div>
            <h1 className="text-2xl font-bold mb-6">Super Admin Dashboard</h1>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard title="Total Companies" value={stats.totalTenants} icon="🏢" color="purple" />
                <StatCard title="Active Subscriptions" value={stats.activeTenants} icon="✅" color="green" />
                <StatCard title="On Trial" value={stats.trialTenants} icon="⏱️" color="yellow" />
                <StatCard title="Total Users" value={stats.totalUsers} icon="👥" color="blue" />
            </div>

            {/* Recent Companies */}
            <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">Recent Companies</h2>
                    <Link to="/super-admin/companies" className="text-purple-400 hover:text-purple-300 text-sm">
                        View all →
                    </Link>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="text-left text-gray-400 text-sm">
                                <th className="pb-3">Company</th>
                                <th className="pb-3">Region</th>
                                <th className="pb-3">Users</th>
                                <th className="pb-3">Status</th>
                                <th className="pb-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {tenants.slice(0, 5).map((tenant) => (
                                <tr key={tenant.id} className="hover:bg-gray-700/50">
                                    <td className="py-3">
                                        <div>
                                            <p className="font-medium">{tenant.name}</p>
                                            <p className="text-sm text-gray-400">{tenant.slug}</p>
                                        </div>
                                    </td>
                                    <td className="py-3">{tenant.region || 'US'}</td>
                                    <td className="py-3">
                                        {tenant.employeeCount} / {tenant.maxEmployees || 10}
                                    </td>
                                    <td className="py-3">
                                        <StatusBadge status={tenant.subscription?.status || 'trial'} />
                                    </td>
                                    <td className="py-3">
                                        <Link
                                            to={`/super-admin/companies/${tenant.id}`}
                                            className="text-purple-400 hover:text-purple-300 text-sm"
                                        >
                                            View
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function StatCard({ title, value, icon, color }: { title: string; value: number; icon: string; color: string }) {
    const colorClasses: Record<string, string> = {
        purple: 'bg-purple-900/50 border-purple-500',
        green: 'bg-green-900/50 border-green-500',
        yellow: 'bg-yellow-900/50 border-yellow-500',
        blue: 'bg-blue-900/50 border-blue-500',
    };

    return (
        <div className={`${colorClasses[color]} border rounded-lg p-4`}>
            <div className="flex items-center gap-3">
                <span className="text-2xl">{icon}</span>
                <div>
                    <p className="text-2xl font-bold">{value}</p>
                    <p className="text-sm text-gray-400">{title}</p>
                </div>
            </div>
        </div>
    );
}

function StatusBadge({ status }: { status: string }) {
    const styles: Record<string, string> = {
        active: 'bg-green-600 text-green-100',
        trial: 'bg-yellow-600 text-yellow-100',
        grace: 'bg-orange-600 text-orange-100',
        readonly: 'bg-red-600 text-red-100',
        expired: 'bg-gray-600 text-gray-100',
    };

    return (
        <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || styles.trial}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
    );
}
